import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { connect } from "react-redux";
import {
  addAnswer,
  addNote,
  addWriteIn,
} from "../../../../redux/survey/actions";
import { toast } from "react-toastify";
import "../components/question.css";
import { useIntl } from "react-intl";

function GridType(props) {
  const intl = useIntl();
  const {
    question,
    answeres,
    addAnswer_,
    survey_theme,
    onSend,
    finishSurvey,
    currentQuestionIndex,
    allQuestions,
    gridHideQuestions,
    edit_answer,
  } = props;

  // State variables
  const [open, setOpen] = useState(false);
  const [gridAnswers, setGridAnswers] = useState({});

  // Check if the input type is multiselect
  const isMultiselect = question.gridInputType === "multiselect";

  // Initialize grid answers
  useEffect(() => {
    const existingAnswer = answeres[question._id];
    if (existingAnswer && existingAnswer.value) {
      // Restore both the grid answers and score exclusions
      const restoredState = {
        ...existingAnswer.value,
        grid_score_exclusions: existingAnswer.grid_score_exclusions || [],
      };
      setGridAnswers(restoredState);
    } else {
      const initialState = {};
      question.gridRows?.forEach((row) => {
        if (isMultiselect) {
          initialState[row._id] = [];
        } else {
          initialState[row._id] = null;
        }
      });
      setGridAnswers(initialState);
    }
  }, [question._id, isMultiselect]);

  // Handle radio button selection
  const handleRadioSelection = (gridRowId, optionId, scoreExclude) => {
    setGridAnswers((prev) => {
      // Ensure grid_score_exclusions is always an array
      const prevExclusions = Array.isArray(prev?.grid_score_exclusions)
        ? prev.grid_score_exclusions
        : [];

      let updatedExclusions;
      if (scoreExclude) {
        // Add gridRowId if not already present
        updatedExclusions = prevExclusions.includes(gridRowId)
          ? prevExclusions
          : [...prevExclusions, gridRowId];
      } else {
        // Remove gridRowId if present
        updatedExclusions = prevExclusions.filter((id) => id !== gridRowId);
      }

      return {
        ...prev,
        [gridRowId]: optionId,
        grid_score_exclusions: updatedExclusions,
      };
    });
  };

  // Handle checkbox selection
  const handleCheckboxSelection = (gridRowId, optionId) => {
    setGridAnswers((prev) => {
      const currentSelections = prev[gridRowId] || [];
      let newSelections;

      if (currentSelections.includes(optionId)) {
        // Remove option if already selected
        newSelections = currentSelections.filter((id) => id !== optionId);
      } else {
        // Add option if not selected
        newSelections = [...currentSelections, optionId];
      }

      // Ensure grid_score_exclusions is always an array
      const prevExclusions = Array.isArray(prev?.grid_score_exclusions)
        ? prev.grid_score_exclusions
        : [];

      // Find all selected options for this grid row
      const selectedOptionIds = newSelections;

      // Find if any selected option for this row has score_exclusion_selection true
      const anyScoreExclude = question.options
        ?.filter((opt) => selectedOptionIds.includes(opt._id))
        ?.some((opt) => opt.score_exclusion_selection);

      let updatedExclusions;
      if (anyScoreExclude) {
        // Add gridRowId if not already present
        updatedExclusions = prevExclusions.includes(gridRowId)
          ? prevExclusions
          : [...prevExclusions, gridRowId];
      } else {
        // Remove gridRowId if present
        updatedExclusions = prevExclusions.filter((id) => id !== gridRowId);
      }

      return {
        ...prev,
        [gridRowId]: newSelections,
        grid_score_exclusions: updatedExclusions,
      };
    });
  };

  // Check if option is selected
  const isOptionSelected = (gridRowId, optionId) => {
    if (isMultiselect) {
      const selections = gridAnswers[gridRowId];
      return Array.isArray(selections) && selections.includes(optionId);
    }
    return gridAnswers[gridRowId] === optionId;
  };

  // Validate and submit answer
  const onSubmitAnswer = () => {
    if (question?.required) {
      for (const row of question.gridRows || []) {
        const rowAnswer = gridAnswers[row._id];

        if (isMultiselect) {
          if (
            !rowAnswer ||
            !Array.isArray(rowAnswer) ||
            rowAnswer.length === 0
          ) {
            toast.error(
              intl.formatMessage({ id: "this-question-is-required" })
            );
            return;
          }
        } else {
          if (!rowAnswer) {
            toast.error(
              intl.formatMessage({ id: "this-question-is-required" })
            );
            return;
          }
        }
      }
    }

    // Save answer to Redux
    // Extract grid_score_exclusions from gridAnswers
    const { grid_score_exclusions, ...actualAnswers } = gridAnswers;

    addAnswer_({
      id: question._id,
      value: actualAnswers,
      type: "grid",
      grid_score_exclusions: grid_score_exclusions || [],
    });

    // Add to history first, then process hide logic
    onSend(gridAnswers);

    // Defer Grid Hide Questions call to allow state updates to complete
    // This ensures the grid Q&A is added to questionHistory before processing hide logic
    setTimeout(() => {
      gridHideQuestions(actualAnswers, question?._id);
    }, 1000);

    setOpen(false);
  };

  // Skip question
  const onSkipQuestion = () => {
    onSend(gridAnswers);
    setOpen(false);
    if (currentQuestionIndex + 1 >= allQuestions?.length) {
      finishSurvey && finishSurvey(true, question);
    }
  };

  // Function to load data from Redux store when component mounts
  const loadData = () => {
    if (edit_answer && Object?.keys(edit_answer)?.length) {
      setOpen(true);
    }
  };

  useEffect(() => {
    loadData();
  }, [answeres]);

  return (
    <>
      <div className="px-4 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-3 h-18">
          <button
            onClick={() => setOpen(true)}
            type="button"
            className="inline-flex items-center justify-center flex-1 px-3 py-2.5 text-sm font-medium leading-4 transition-all duration-200 bg-transparent border border-purple-600 rounded-full shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-600"
            style={{ color: survey_theme?.primaryColor }}
          >
            <svg
              className="w-5 height-1 mr-3"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
            {intl.formatMessage({ id: "select" })}
          </button>
          {!question?.required ? (
            <button
              onClick={onSkipQuestion}
              type="button"
              className="inline-flex items-center justify-center px-3 py-2.5 text-sm font-medium leading-4 text-gray-600 transition-all duration-200 bg-transparent border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            >
              {intl.formatMessage({ id: "skip" })}
            </button>
          ) : null}
        </div>
      </div>

      {open ? (
        <div>
          <Dialog
            fullScreen
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="mobile-view-modal"
            id="mobile-view-modal"
          >
            <DialogTitle id="mobile-view-modal-title">
              <div className="modal-header">
                <h5 className="mr-12 text-lg font-bold truncate">
                  {intl.formatMessage({ id: "select" })}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                >
                  <span className="sr-only">
                    {intl.formatMessage({ id: "close" })}
                  </span>
                </button>
              </div>
            </DialogTitle>

            <DialogContent id="mobile-view-modal-body">
              <div className="divide-y divide-gray-200">
                {question.gridRows?.map((gridRow) => (
                  <div key={gridRow._id} className="py-4 px-3">
                    {/* Row Title */}
                    <div className="mb-3">
                      <h3 className="text-base font-medium text-gray-900">
                        {gridRow.text}
                      </h3>
                    </div>

                    {/* Options in a 2x2 grid layout */}
                    <div className="grid grid-cols-2 gap-2">
                      {question.options?.map((option) => (
                        <div
                          key={option._id}
                          className={`flex items-center p-2 rounded-lg border ${
                            isOptionSelected(gridRow._id, option._id)
                              ? "bg-orange-50 border-orange-200"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <label
                            htmlFor={`${gridRow._id}-${option._id}`}
                            className="flex items-center cursor-pointer w-full"
                          >
                            <span className="text-sm font-medium text-gray-800 mr-2">
                              {option.text}
                            </span>
                            {isMultiselect ? (
                              <input
                                type="checkbox"
                                id={`${gridRow._id}-${option._id}`}
                                checked={isOptionSelected(
                                  gridRow._id,
                                  option._id
                                )}
                                onChange={() =>
                                  handleCheckboxSelection(
                                    gridRow._id,
                                    option._id
                                  )
                                }
                                className="w-5 h-5 border-gray-300 rounded focus:ring-2 ms-auto"
                                style={{
                                  accentColor:
                                    survey_theme?.primaryColor || "#36473f",
                                }}
                              />
                            ) : (
                              <input
                                type="radio"
                                id={`${gridRow._id}-${option._id}`}
                                name={`grid-row-${gridRow._id}`}
                                checked={isOptionSelected(
                                  gridRow._id,
                                  option._id
                                )}
                                onChange={() =>
                                  handleRadioSelection(
                                    gridRow._id,
                                    option._id,
                                    option?.score_exclusion_selection
                                  )
                                }
                                className="w-5 h-5 border-gray-300 rounded-full focus:ring-2 ms-auto"
                                style={{
                                  accentColor:
                                    survey_theme?.primaryColor || "#36473f",
                                }}
                              />
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>

            <DialogActions id="mobile-view-modal-footer">
              <div className="flex items-center justify-center space-x-4">
                <button
                  style={{
                    paddingTop: "0.75rem",
                    paddingBottom: "0.75rem",
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                  }}
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center flex-1 w-full text-base font-semibold text-gray-600 transition-all duration-200 bg-gray-300 rounded-full shadow-lg hover:text-white focus:text-white hover:bg-gray-600 focus:bg-gray-600"
                >
                  {intl.formatMessage({ id: "discard" })}
                </button>
                <button
                  style={{
                    backgroundColor: survey_theme?.primaryColor,
                    paddingTop: "0.75rem",
                    paddingBottom: "0.75rem",
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                  }}
                  type="button"
                  onClick={onSubmitAnswer}
                  className="flex items-center justify-center flex-1 w-full text-base font-semibold text-white transition-all duration-200 rounded-full shadow-lg hover:shadow-xl focus:shadow-xl"
                >
                  {intl.formatMessage({ id: "save" })}
                </button>
              </div>
            </DialogActions>
          </Dialog>
        </div>
      ) : null}
    </>
  );
}

// This is the mapStateToProps function that extracts the necessary state from the Redux store
const mapStateToProps = (state) => {
  return {
    answeres: state.survey.answeres,
    edit_answer: state.survey.edit_answer,
  };
};

// This is the mapDispatchToProps function that binds the dispatch function to action creators
const mapDispatchToProps = (dispatch) => {
  return {
    addAnswer_: (data) => dispatch(addAnswer(data)),
    addNote_: (data) => dispatch(addNote(data)),
    addWriteIn_: (data) => dispatch(addWriteIn(data)),
  };
};

// Connects the GridType component to the Redux store
export default connect(mapStateToProps, mapDispatchToProps)(GridType);
